import type { ImageBleed } from 'types';
import type {
  ImageGridModule,
  ImageWithMetadata as Item
} from 'types/capi/article';

import ImageWithMetadata from '~/components/ImageWithMetadata';

type Props = {
  module: ImageGridModule;
};

function isPortrait(ratio?: string) {
  if (!ratio) return false;
  const [w, h] = ratio.split(':').map(Number);

  return w != null && h != null && h > w;
}

const portraitWidth = 'sm:max-w-[340px] md:max-w-[456px] lg:max-w-[552px]';

function getContainerWidthClass(items: Item[]) {
  const imageCount = Math.min(items.length, 2);
  if (imageCount === 2) return 'w-full';

  const singleImage = items[0];
  if (!singleImage) return 'w-full';

  const mobilePortrait = isPortrait(singleImage.mobile.ratio);
  const desktopPortrait = isPortrait(singleImage.desktop.ratio);

  if (mobilePortrait && desktopPortrait) return portraitWidth;
  if (mobilePortrait) return `${portraitWidth} md:max-w-none`;
  if (desktopPortrait) return `md:max-w-[456px] lg:max-w-[552px]`;

  return 'w-full';
}

export function ImageGridCore({
  bleed,
  items
}: {
  bleed?: ImageBleed;
  items: Item[];
}) {
  const imageCount = Math.min(items.length, 2);
  const hasBleed = bleed && bleed !== 'none';
  const containerWidthClass = hasBleed
    ? 'w-full'
    : getContainerWidthClass(items);

  return (
    <div
      className={`grid gap-x-4 gap-y-6 md:gap-6 ${
        hasBleed ? '' : 'max-w-desktop'
      } ${
        imageCount === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
      } ${containerWidthClass}`}
    >
      {items.map((item, index) => (
        <ImageWithMetadata
          key={`image-grid-item-${index}`}
          bleed={bleed}
          item={item}
          index={index}
        />
      ))}
    </div>
  );
}

export default function ImageGrid({ module }: Props) {
  const { display_type, items } = module;

  if (display_type === 'full-bleed') {
    const item = items[0];
    if (!item) return null;

    return (
      <ImageWithMetadata
        bleed="full"
        index={0}
        item={item}
        pagePosition="Full Bleed Image"
      />
    );
  }

  return (
    <div className="flex w-full items-center justify-center px-4 md:px-8">
      <ImageGridCore items={items} />
    </div>
  );
}
